import User from '@this/domain/user/user2';
// TODO: search_query.tsでTravelerListで持っているため変換する
import type Traveler from '@this/domain/traveler/traveler';
import TravelerInformation from '@this/domain/traveler_information';
import moment from 'moment';

/**
 * 検索結果サマリーデータから旅程説明文を生成
 */
export const createItineraryDescription = (summaryData: { [key: string]: string }): string => {
  return Object.keys(summaryData)
    .map(key => `${key}: ${summaryData[key]}`)
    .join('\n');
};

/**
 * 初期出張者リストを生成
 */
export const createInitialTravelers = (peoplenum: number, user: User, members: User[]): TravelerInformation[] => {
  const travelers = Array.from({ length: peoplenum }, () => new TravelerInformation({}));
  const selfTraveler = new TravelerInformation({ type: 'self' });
  selfTraveler.assignFromUser(user);
  selfTraveler.shinkansenTicketType = 'normal';
  selfTraveler.shinkansenSeatType = 'free';
  selfTraveler.shinkansenSeat = '指定なし';
  travelers[0] = selfTraveler;

  if (peoplenum > 1) {
    for (let i = 1, end = peoplenum - 1, asc = end >= 1; asc ? i <= end : i >= end; asc ? (i += 1) : (i -= 1)) {
      const type = user.showOtherMembers && members.length > 0 ? 'member' : 'companion';
      travelers[i].type = type;
      travelers[i].shinkansenTicketType = 'normal';
      travelers[i].shinkansenSeatType = 'free';
      travelers[i].shinkansenSeat = '指定なし';
    }
  }

  return travelers;
};

const convertTravelerToTravelerInformation = (
  traveler: Traveler,
  user: User,
  members: User[]
): TravelerInformation => {
  const travelerWithUserId = traveler as Traveler & {
    user_id?: number | string;
    userId?: number | string;
    user?: {
      id?: number | string;
      name?: string;
      email?: string;
      employeeNumber?: string;
      employee_number?: string;
      firstName?: string;
      lastName?: string;
      firstNameKana?: string;
      lastNameKana?: string;
      firstNameRoman?: string;
      lastNameRoman?: string;
      birthday?: moment.Moment;
      gender?: string;
      tel?: string;
      department?: User['department'];
    };
    employee_number?: string;
    employeeNumber?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    department?: User['department'];
  };
  const travelerType =
    traveler.type === 'self' || traveler.type === 'member' || traveler.type === 'companion'
      ? traveler.type
      : 'companion';
  if (travelerType === 'self') {
    const info = new TravelerInformation({ type: 'self' });
    info.assignFromUser(user);
    info.id = traveler.travelerInformationId || user.id;
    info.name = traveler.name;
    info.userName = traveler.name || user.name;
    info.birthday = traveler.birthday ? moment(traveler.birthday) : user.birthday;
    info.airSeat = traveler.airSeat;
    info.shinkansenTicketType = traveler.shinkansenTicketType;
    info.shinkansenSeatType = traveler.shinkansenSeatType;
    info.shinkansenSeat = traveler.shinkansenSeat;
    info.shinkansenSeatInfo = traveler.requestShinkansenSeat;
    info.exicCardId = traveler.exicCardId ?? user.exicCardId;
    info.flightTel = traveler.flightTel || info.flightTel;
    return info;
  }
  const rawUserId =
    travelerWithUserId.user_id || travelerWithUserId.userId || travelerWithUserId.user?.id || traveler.id;
  const userId =
    travelerType === 'companion' || rawUserId == null || rawUserId === '' ? undefined : Number(rawUserId);
  const member = userId ? members.find(m => m.id === userId) : undefined;
  const travelerUser =
    member ||
    (userId
      ? new User({
          id: userId,
          name: travelerWithUserId.user?.name || traveler.name,
          email: travelerWithUserId.user?.email || travelerWithUserId.email,
          employeeNumber:
            travelerWithUserId.user?.employeeNumber ||
            travelerWithUserId.user?.employee_number ||
            travelerWithUserId.employeeNumber ||
            travelerWithUserId.employee_number,
          tel: travelerWithUserId.user?.tel || travelerWithUserId.tel,
          nationalizedTel: travelerWithUserId.user?.tel || travelerWithUserId.tel,
          firstName: travelerWithUserId.user?.firstName || travelerWithUserId.firstName,
          lastName: travelerWithUserId.user?.lastName || travelerWithUserId.lastName,
          firstNameKana: travelerWithUserId.user?.firstNameKana || traveler.firstNameKana,
          lastNameKana: travelerWithUserId.user?.lastNameKana || traveler.lastNameKana,
          firstNameRoman: travelerWithUserId.user?.firstNameRoman || traveler.firstNameRoman,
          lastNameRoman: travelerWithUserId.user?.lastNameRoman || traveler.lastNameRoman,
          birthday: travelerWithUserId.user?.birthday,
          gender: travelerWithUserId.user?.gender,
          department: travelerWithUserId.user?.department || travelerWithUserId.department
        })
      : undefined);
  return new TravelerInformation({
    id: traveler.travelerInformationId || userId,
    name: traveler.name,
    type: travelerType,
    userId,
    userName: traveler.name,
    lastNameRoman: traveler.lastNameRoman,
    firstNameRoman: traveler.firstNameRoman,
    middleNameRoman: traveler.middleNameRoman,
    lastNameKana: traveler.lastNameKana,
    firstNameKana: traveler.firstNameKana,
    birthday: traveler.birthday ? moment(traveler.birthday) : undefined,
    flightLastName: traveler.flightLastName,
    flightFirstName: traveler.flightFirstName,
    flightMiddleName: traveler.flightMiddleName,
    flightLastNameRoman: traveler.flightLastNameRoman,
    flightFirstNameRoman: traveler.flightFirstNameRoman,
    flightMiddleNameRoman: traveler.flightMiddleNameRoman,
    flightLastNameKana: traveler.flightLastNameKana,
    flightFirstNameKana: traveler.flightFirstNameKana,
    flightBirthday: traveler.flightBirthday ? moment(traveler.flightBirthday) : undefined,
    flightGender: traveler.flightGender,
    flightTel: traveler.flightTel,
    airSeat: traveler.airSeat,
    shinkansenTicketType: traveler.shinkansenTicketType,
    shinkansenSeatType: traveler.shinkansenSeatType,
    shinkansenSeat: traveler.shinkansenSeat,
    shinkansenSeatInfo: traveler.requestShinkansenSeat,
    mileageNumber: traveler.mileageNumber,
    exicNumber: traveler.exicNumber,
    exicPassword: traveler.exicPassword,
    exicCardId: traveler.exicCardId,
    passportNumber: traveler.passportNumber,
    passportExpire: traveler.passportExpire,
    passportIssuedCountry: traveler.passportIssuedCoutry,
    immigrationCardCreation: traveler.immigrationCardCreation,
    visaApplication: traveler.visaApplication,
    nationalityId: traveler.nationalityId,
    user: travelerUser
  });
};

export const convertTravelerListToTravelerInformations = (
  travelers: Traveler[],
  peoplenum: number,
  user: User,
  members: User[]
): TravelerInformation[] => {
  const initialTravelers = createInitialTravelers(peoplenum, user, members);
  travelers.forEach((traveler, i) => {
    if (initialTravelers[i]) {
      initialTravelers[i] = convertTravelerToTravelerInformation(traveler, user, members);
    }
  });
  return initialTravelers;
};
